import { StatelessSearchbarItemContext } from "components/community-search/search-bar/stateless-searchbar-item";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import * as React from "react";
import { useCanSeeReferralSourceOwners, usePreferences } from "store/selectors/hooks";
import { ReferenceDashboardAspect } from "store/slices/reference-dashboard";

type Direction = "ASC" | "DESC";

type ReferenceSearchSortValue = {
    sort: string;
    sortDirection: string;
};

export type ReferenceSearchSortValues = {
    contact: ReferenceSearchSortValue;
    business: ReferenceSearchSortValue;
    community: ReferenceSearchSortValue;
    organization: ReferenceSearchSortValue;
};

interface ReferenceSearchSortProps {
    aspect: ReferenceDashboardAspect;
    values: ReferenceSearchSortValues;
    onChange: (
        aspect: ReferenceDashboardAspect,
        sort: string,
        sortDirection: string,
    ) => void;
}

interface SortField {
    key: string;
    title: string;
    ascLabel: string;
    descLabel: string;
    primary: Direction;
}

const alpha = (key: string, title: string): SortField => ({
    key,
    title,
    ascLabel: "A–Z",
    descLabel: "Z–A",
    primary: "ASC",
});

const dated = (key: string, title: string): SortField => ({
    key,
    title,
    ascLabel: "Oldest",
    descLabel: "Newest",
    primary: "DESC",
});

const counted = (key: string, title: string): SortField => ({
    key,
    title,
    ascLabel: "Least",
    descLabel: "Most",
    primary: "DESC",
});

const dateAddedField = dated("dateAdded", "Date added");
const lastUpdatedField = dated("updatedAt", "Last updated");
const cityField = alpha("city", "City");
const numberOfReferralsField = counted("numberOfReferrals", "Number of referrals");
const mostRecentReferralField = dated("mostRecentReferral", "Most recent referral");
const assignedToField = alpha("assignedTo", "Assigned to");

const allSortFields: Record<ReferenceDashboardAspect, SortField[]> = {
    contact: [
        alpha("name", "Contact name"),
        alpha("role", "Role/title"),
        dateAddedField,
        lastUpdatedField,
        alpha("organizationName", "Organization name"),
        alpha("organizationType", "Organization type"),
        cityField,
        numberOfReferralsField,
        mostRecentReferralField,
        assignedToField,
    ],
    business: [
        alpha("name", "Business name"),
        dateAddedField,
        lastUpdatedField,
        alpha("organizationType", "Organization type"),
        cityField,
        numberOfReferralsField,
        mostRecentReferralField,
        assignedToField,
    ],
    community: [
        alpha("name", "Community name"),
        dateAddedField,
        lastUpdatedField,
        cityField,
        numberOfReferralsField,
        mostRecentReferralField,
    ],
    organization: [
        alpha("name", "Organization name"),
        dateAddedField,
        lastUpdatedField,
        cityField,
        numberOfReferralsField,
        mostRecentReferralField,
    ],
};

export const describeReferenceSort = (
    aspect: ReferenceDashboardAspect,
    value: ReferenceSearchSortValue,
): string => {
    const field =
        allSortFields[aspect].find(f => f.key === value.sort) ?? allSortFields[aspect][0];
    const directionLabel =
        value.sortDirection === "ASC" ? field.ascLabel : field.descLabel;
    return `${field.title} (${directionLabel})`;
};

export const ReferenceSearchSort: React.FC<ReferenceSearchSortProps> = props => {
    const { aspect, values, onChange } = props;
    const closeCalloutCallback = React.useContext(StatelessSearchbarItemContext);

    const chooseOption = (sortOption: string, direction: Direction) => {
        if (closeCalloutCallback) {
            closeCalloutCallback();
        }
        onChange(aspect, sortOption, direction);
    };

    const { showReferralCityOnMainTable, showReferralLastUpdatedDateOnMainTable } =
        usePreferences();
    const assignedToVisible = useCanSeeReferralSourceOwners();

    const isHidden = (key: string): boolean => {
        if (key === "updatedAt") return !showReferralLastUpdatedDateOnMainTable;
        if (key === "city") return !showReferralCityOnMainTable;
        if (key === "assignedTo") return !assignedToVisible;
        return false;
    };

    const currentSorts = allSortFields[aspect].filter(o => !isHidden(o.key));
    const value = values[aspect];

    return (
        <div className="w-80">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Sort by
            </div>
            <ul className="mt-3 py-1">
                {currentSorts.map(option => {
                    const isSelected = value.sort === option.key;
                    const ascSelected = isSelected && value.sortDirection === "ASC";
                    const descSelected = isSelected && value.sortDirection === "DESC";

                    const ascButton = (
                        <SecondaryCompactButton
                            key="asc"
                            selected={ascSelected}
                            onClick={e => {
                                e.preventDefault();
                                chooseOption(option.key, "ASC");
                            }}>
                            {option.ascLabel}
                        </SecondaryCompactButton>
                    );
                    const descButton = (
                        <SecondaryCompactButton
                            key="desc"
                            selected={descSelected}
                            onClick={e => {
                                e.preventDefault();
                                chooseOption(option.key, "DESC");
                            }}>
                            {option.descLabel}
                        </SecondaryCompactButton>
                    );

                    return (
                        <li
                            key={`${aspect}_${option.key}`}
                            className="-mx-2 flex items-center justify-between gap-3 rounded px-2 py-2">
                            <span
                                className={`text-sm ${
                                    isSelected
                                        ? "font-semibold text-gray-900"
                                        : "text-gray-700"
                                }`}>
                                {option.title}
                            </span>
                            <div className="flex items-center gap-1">
                                {option.primary === "DESC"
                                    ? [descButton, ascButton]
                                    : [ascButton, descButton]}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
